import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Calendar, CheckCircle, Truck, Info, Send, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import { de } from "date-fns/locale";
import { format, addDays, isBefore, isAfter, startOfDay } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const IMAGES = [
  "https://customer-assets.emergentagent.com/job_anhanger-gear/artifacts/qh803v2j_IMG_6657.jpg",
  "https://customer-assets.emergentagent.com/job_anhanger-gear/artifacts/9iynxeht_IMG_6656%20%281%29.jpg",
  "https://customer-assets.emergentagent.com/job_anhanger-gear/artifacts/nyxpfzul_IMG_6658.jpg",
  "https://customer-assets.emergentagent.com/job_anhanger-gear/artifacts/z4rwapy6_IMG_6659.jpg"
];

const PRICING = {
  "ohne-plane": {
    "4h": 27.00,
    "24h": 34.00,
    "weekend": 49.00,
    "week": 119.00
  },
  "mit-plane": {
    "4h": 31.00,
    "24h": 39.00,
    "weekend": 58.00,
    "week": 135.00
  }
};

const PERIOD_LABELS = {
  "4h": "4 Stunden",
  "24h": "24 Stunden",
  "weekend": "Wochenende",
  "week": "1 Woche"
};

const KAUTION = 80.00;

const specifications = [
  { label: "Marke", value: "Temared" },
  { label: "Modell", value: "PRO BREAK 2612 C" },
  { label: "Zulässiges Gesamtgewicht", value: "750 kg" },
  { label: "Eigengewicht", value: "237 kg" },
  { label: "Nutzlast", value: "513 kg" },
  { label: "Ladefläche", value: "2640 x 1260 mm" },
  { label: "Außenmaße", value: "3920 x 1770 x 1880 mm" },
  { label: "Farbe", value: "Silber" },
  { label: "Baujahr", value: "2022" },
  { label: "Heckklappe", value: "Ja" }
];

const planeSpecs = [
  { label: "Ausstattung", value: "Aufsatzgestell 1100 mm + graue Plane" },
  { label: "Aufbau-/Planenmaße", value: "2690 x 1307 x 1100 mm" },
  { label: "Planenfarbe", value: "Grau" }
];

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0 },
};

export default function ProductPage() {
  const [selectedImage, setSelectedImage] = useState(0);
  const [selectedVariant, setSelectedVariant] = useState("ohne-plane");
  const [selectedPeriod, setSelectedPeriod] = useState("24h");
  const [selectedDate, setSelectedDate] = useState(null);
  const [blockedDates, setBlockedDates] = useState([]);
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    message: ""
  });

  useEffect(() => {
    fetchBlockedDates();
  }, []);

  const fetchBlockedDates = async () => {
    try {
      const res = await axios.get(`${API}/blocked-dates`);
      setBlockedDates(res.data.map(d => new Date(d.date)));
    } catch (err) {
      console.error("Error fetching blocked dates:", err);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const isDateBlocked = (date) => {
    const today = startOfDay(new Date());
    if (isBefore(date, today)) return true;
    return blockedDates.some(blocked => 
      format(blocked, 'yyyy-MM-dd') === format(date, 'yyyy-MM-dd')
    );
  };

  const calculatePrice = () => {
    return PRICING[selectedVariant][selectedPeriod];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate) {
      toast.error("Bitte wählen Sie ein Startdatum");
      return;
    }

    setLoading(true);

    try {
      const bookingData = {
        ...formData,
        variant: selectedVariant,
        rental_period: selectedPeriod,
        start_date: format(selectedDate, 'yyyy-MM-dd')
      };

      await axios.post(`${API}/bookings`, bookingData);
      
      toast.success("Buchungsanfrage gesendet!", {
        description: "Wir melden uns schnellstmöglich bei Ihnen."
      });

      // Reset form
      setFormData({
        first_name: "",
        last_name: "",
        email: "",
        phone: "",
        message: ""
      });
      setSelectedDate(null);
    } catch (error) {
      console.error("Error submitting booking:", error);
      toast.error("Fehler beim Senden", {
        description: "Bitte versuchen Sie es später erneut."
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="product-page">
      {/* Hero */}
      <section className="bg-zinc-50 py-12 border-b border-zinc-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            transition={{ duration: 0.5 }}
          >
            <p className="text-sm text-orange-600 font-medium mb-2">Anhänger mieten</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-zinc-950 tracking-tight">
              PKW-Anhänger 750 kg
            </h1>
            <p className="text-lg text-zinc-600 mt-2">Temared PRO BREAK 2612 C</p>
          </motion.div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Left - Images */}
            <div>
              <div className="rounded-2xl overflow-hidden bg-zinc-100 mb-4">
                <img
                  src={IMAGES[selectedImage]}
                  alt="PKW-Anhänger"
                  className="w-full aspect-[4/3] object-cover"
                />
              </div>
              <div className="grid grid-cols-5 gap-2">
                {IMAGES.map((img, idx) => (
                  <button
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`rounded-lg overflow-hidden border-2 transition-colors ${
                      selectedImage === idx ? "border-orange-500" : "border-transparent"
                    }`}
                  >
                    <img src={img} alt={`Bild ${idx + 1}`} className="w-full aspect-square object-cover" />
                  </button>
                ))}
              </div>
            </div>

            {/* Right - Details & Booking */}
            <div>
              {/* Description */}
              <div className="mb-8">
                <h2 className="text-xl font-semibold text-zinc-900 mb-4">Beschreibung</h2>
                <p className="text-zinc-600 leading-relaxed">
                  Vielseitiger PKW-Anhänger für private und gewerbliche Transporte. 
                  Geeignet für kleinere Umzüge, Möbel, Baumaterial, Gartenabfälle und sonstige sperrige Güter. 
                  Kompakt, robust und einfach zu rangieren.
                </p>
                <ul className="mt-4 space-y-2">
                  {[
                    "Ideal für kleinere Umzüge",
                    "Geeignet für Möbel, Baumaterial, Gartenabfälle",
                    "Variante mit Plane schützt vor Regen und Schmutz",
                    "Abholung in Sonnberg im Mühlkreis",
                    "Flexible Abholzeiten nach Vereinbarung"
                  ].map((feature, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-zinc-700">
                      <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Variant Selection */}
              <div className="mb-8 p-6 bg-zinc-50 rounded-xl border border-zinc-100">
                <h3 className="font-semibold text-zinc-900 mb-4">Variante wählen</h3>
                <RadioGroup value={selectedVariant} onValueChange={setSelectedVariant}>
                  <div className="space-y-3">
                    <label
                      className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedVariant === "ohne-plane" 
                          ? "border-orange-500 bg-orange-50" 
                          : "border-zinc-200 bg-white hover:bg-zinc-50"
                      }`}
                    >
                      <RadioGroupItem value="ohne-plane" id="ohne-plane" className="mt-1" />
                      <div className="flex-1">
                        <p className="font-medium text-zinc-900">Ohne Plane</p>
                        <p className="text-sm text-zinc-500">Offene Ausführung für sperrige Güter</p>
                      </div>
                    </label>
                    <label
                      className={`flex items-start gap-4 p-4 rounded-lg border cursor-pointer transition-colors ${
                        selectedVariant === "mit-plane" 
                          ? "border-orange-500 bg-orange-50" 
                          : "border-zinc-200 bg-white hover:bg-zinc-50"
                      }`}
                    >
                      <RadioGroupItem value="mit-plane" id="mit-plane" className="mt-1" />
                      <div className="flex-1">
                        <p className="font-medium text-zinc-900">Mit Aufsatzgestell und grauer Plane</p>
                        <p className="text-sm text-zinc-500">Schutz vor Regen, Wind und Schmutz</p>
                      </div>
                    </label>
                  </div>
                </RadioGroup>
              </div>

              {/* Period Selection */}
              <div className="mb-8 p-6 bg-zinc-50 rounded-xl border border-zinc-100">
                <h3 className="font-semibold text-zinc-900 mb-4">Mietzeitraum wählen</h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {Object.entries(PERIOD_LABELS).map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setSelectedPeriod(key)}
                      data-testid={`period-${key}`}
                      className={`p-3 rounded-lg border text-center transition-colors cursor-pointer ${
                        selectedPeriod === key
                          ? "border-orange-500 bg-orange-50"
                          : "border-zinc-200 bg-white hover:bg-zinc-50"
                      }`}
                    >
                      <p className="text-xs text-zinc-500">{label}</p>
                      <p className="text-lg font-bold text-zinc-900">
                        €{PRICING[selectedVariant][key]}
                      </p>
                    </button>
                  ))}
                </div>
                <p className="text-sm text-zinc-500 mt-4 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Kaution: €{KAUTION} (bei Abholung)
                </p>
              </div>

              {/* Price Summary */}
              <div className="mb-8 p-6 bg-orange-50 rounded-xl border border-orange-100">
                <div className="flex justify-between items-center">
                  <div>
                    <p className="text-sm text-zinc-600">Mietpreis</p>
                    <p className="text-3xl font-bold text-orange-600">€{calculatePrice()}</p>
                    <p className="text-sm text-zinc-500">+ €{KAUTION} Kaution</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-zinc-600">{PERIOD_LABELS[selectedPeriod]}</p>
                    <p className="text-sm text-zinc-500">
                      {selectedVariant === "mit-plane" ? "Mit Plane" : "Ohne Plane"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Booking Form */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.5 }}
            className="mt-16"
          >
            <div className="bg-zinc-50 rounded-2xl p-8 border border-zinc-100">
              <h2 className="text-2xl font-bold text-zinc-950 mb-2">Buchungsanfrage senden</h2>
              <p className="text-zinc-600 mb-8">
                Wählen Sie Ihr Wunschdatum und senden Sie Ihre Anfrage. Wir bestätigen die Buchung 
                und vereinbaren die genaue Abholzeit.
              </p>

              <form onSubmit={handleSubmit}>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Calendar */}
                  <div>
                    <h3 className="font-semibold text-zinc-900 mb-4 flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-orange-500" />
                      Startdatum wählen
                    </h3>
                    <div className="bg-white rounded-xl p-4 border border-zinc-200 inline-block">
                      <CalendarComponent
                        mode="single"
                        selected={selectedDate}
                        onSelect={setSelectedDate}
                        disabled={isDateBlocked}
                        locale={de}
                        className="rounded-md"
                      />
                    </div>
                    {selectedDate && (
                      <p className="mt-4 text-sm text-zinc-600">
                        Gewähltes Datum: <strong>{format(selectedDate, 'dd. MMMM yyyy', { locale: de })}</strong>
                      </p>
                    )}
                  </div>

                  {/* Contact Form */}
                  <div className="space-y-5">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="first_name">Vorname *</Label>
                        <Input
                          id="first_name"
                          name="first_name"
                          required
                          value={formData.first_name}
                          onChange={handleChange}
                          placeholder="Max"
                          className="border-zinc-200"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name">Nachname *</Label>
                        <Input
                          id="last_name"
                          name="last_name"
                          required
                          value={formData.last_name}
                          onChange={handleChange}
                          placeholder="Mustermann"
                          className="border-zinc-200"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="email">E-Mail *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="max@beispiel.at"
                        className="border-zinc-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="phone">Telefon *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        required
                        value={formData.phone}
                        onChange={handleChange}
                        placeholder="+43 ..."
                        className="border-zinc-200"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="message">Nachricht (optional)</Label>
                      <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleChange}
                        placeholder="Anmerkungen, Wunschzeit für Abholung, etc."
                        className="border-zinc-200 resize-none"
                        rows={3}
                      />
                    </div>

                    <div className="bg-zinc-100 rounded-lg p-4 text-sm text-zinc-600">
                      <p className="font-medium text-zinc-900 mb-1">Hinweis zur Abholung</p>
                      <p>Die genaue Abholzeit wird nach Bestätigung der Buchung vereinbart. Zahlung erfolgt bei Abholung.</p>
                    </div>

                    <Button
                      type="submit"
                      disabled={loading || !selectedDate}
                      className="w-full bg-orange-600 hover:bg-orange-700 text-white py-6 text-lg"
                    >
                      {loading ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Wird gesendet...
                        </>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Buchungsanfrage senden
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </form>
            </div>
          </motion.div>

          {/* Specifications */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.5 }}
            className="mt-16"
          >
            <h2 className="text-2xl font-bold text-zinc-950 mb-6">Technische Daten</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Base Specs */}
              <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                <div className="bg-zinc-50 px-6 py-4 border-b border-zinc-200">
                  <h3 className="font-semibold text-zinc-900">Anhänger</h3>
                </div>
                <div>
                  {specifications.map((spec, idx) => (
                    <div
                      key={idx}
                      className={`flex justify-between px-6 py-3 text-sm ${
                        idx % 2 === 0 ? "bg-zinc-50" : "bg-white"
                      }`}
                    >
                      <span className="text-zinc-600">{spec.label}</span>
                      <span className="font-medium text-zinc-900">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Plane Specs */}
              <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
                <div className="bg-orange-50 px-6 py-4 border-b border-orange-100">
                  <h3 className="font-semibold text-zinc-900">Aufsatzgestell + Plane</h3>
                  <p className="text-sm text-zinc-500">Zusatzdaten für Variante mit Plane</p>
                </div>
                <div>
                  {planeSpecs.map((spec, idx) => (
                    <div
                      key={idx}
                      className={`flex justify-between px-6 py-3 text-sm ${
                        idx % 2 === 0 ? "bg-zinc-50" : "bg-white"
                      }`}
                    >
                      <span className="text-zinc-600">{spec.label}</span>
                      <span className="font-medium text-zinc-900">{spec.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
